// Pure helpers of the Stats page: axis steps, running totals and the short labels its charts print. No DOM, so
// the tests cover them directly.

/** 1, 2 or 5 times a power of ten: axis steps a reader can add up */
export function niceStep(span: number, ticks: number): number {
  if (!(span > 0) || !(ticks > 0)) return 1;
  const raw = span / ticks;
  const p = 10 ** Math.floor(Math.log10(raw));
  const m = raw / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
}

/** The axis of values spanning [lo, hi] (zero always on it): its rounded bounds and its ticks */
export function axis(lo: number, hi: number, ticks = 4): { lo: number; hi: number; ticks: number[] } {
  const rawLo = Math.min(0, lo);
  const rawHi = Math.max(0, hi);
  const step = niceStep(rawHi - rawLo || 1, ticks);
  const bottom = Math.floor(rawLo / step) * step;
  const ceil = Math.ceil(rawHi / step) * step;
  const top = ceil > bottom ? ceil : bottom + step;
  const out: number[] = [];
  for (let v = bottom; v <= top + step / 2; v += step) out.push(Math.abs(v) < step / 1e6 ? 0 : v);
  return { lo: bottom, hi: top, ticks: out };
}

/** Running total, same length */
export function cumulative(values: readonly number[]): number[] {
  const out: number[] = [];
  let acc = 0;
  for (const v of values) out.push((acc += v));
  return out;
}

/** 1.2M, 340K, 12, 0.5: three significant figures at most, no trailing zeros */
export function compact(v: number): string {
  const a = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  const trim = (s: string) => s.replace(/\.0+$|(\.\d*?)0+$/, '$1');
  if (a >= 1e9) return `${sign}${trim((a / 1e9).toFixed(2))}B`;
  if (a >= 1e6) return `${sign}${trim((a / 1e6).toFixed(2))}M`;
  if (a >= 1e3) return `${sign}${trim((a / 1e3).toFixed(a >= 1e5 ? 0 : 1))}K`;
  if (a >= 100) return `${sign}${a.toFixed(0)}`;
  if (a >= 10) return `${sign}${trim(a.toFixed(1))}`;
  return `${sign}${trim(a.toFixed(2))}`;
}

export const money = (v: number): string => `${v < 0 ? '-' : ''}$${compact(Math.abs(v))}`;
export const signedMoney = (v: number): string => `${v < 0 ? '-' : '+'}$${compact(Math.abs(v))}`;
export const count = (v: number): string => (Math.abs(v) < 1e3 ? Math.round(v).toLocaleString('en-US') : compact(v));
export const units = (symbol: string) => (v: number) => `${compact(v)} ${symbol}`;

/** A bucket's time on the axis or in a tooltip: the hour inside a day, the date beyond */
export function timeLabel(ms: number, bucketMs: number, long = false): string {
  const d = new Date(ms);
  if (bucketMs < 86_400_000) {
    const day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const hour = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return long ? `${day} ${hour}` : hour;
  }
  return d.toLocaleDateString('en-GB', long ? { day: 'numeric', month: 'short', year: 'numeric' } : { day: 'numeric', month: 'short' });
}
